import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import {environment} from "../../environments/environment";
import {LoginDto, LoginViewModel} from "../model/login.model";
import {ApiService} from "./api.service";

interface CookieLoginModel extends LoginViewModel {
  expiresAt: number
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private cookieService: CookieService;
  private httpClient: HttpClient;

  constructor(private injector: Injector, private apiService: ApiService) {
    this.cookieService = this.injector.get(CookieService);
    this.httpClient = this.injector.get(HttpClient);
  }

  /**
   * Login user with credentials
   * @param username Username (without domain)
   * @param password Password
   * @returns Observable with login response
   */
  login(username: string, password: string): Observable<Boolean> {
    const credentials: LoginDto = {
      username: username,
      password: password
    }
    return this.apiService.login(credentials)
      .pipe(
        map((response: LoginViewModel) => {
          // Use session_ttl from API response (in seconds) to set cookie expiration
          const expirationDate = new Date(Date.now() + response.session_ttl * 1000);

          // Store expiration timestamp in cookie data for local validation
          const loginData: CookieLoginModel = {
            ...response,
            expiresAt: expirationDate.getTime()
          };

          this.cookieService.set('oadr_login', JSON.stringify(loginData), {
            expires: expirationDate,
          });
          this.cookieService.set('enoc_session', response.token, {
            expires: expirationDate,
            path: '/',
            domain: environment.cookieDomain,
            sameSite: 'Lax',
        });
          return true;
        }),
        catchError((err: Error | HttpErrorResponse) => {
          return throwError(() => err);
        })
      );
  }

  /**
   * Check if user is authorized by cookie presence
   * @returns Boolean indicating if user is authorized
   */
  public checkAuthorization(): boolean {
    return !!this.cookieService.get('oadr_login');
  }

  /**
   * Check if the current session is still valid based on cookie expiration
   * @returns Boolean indicating if session is valid (not expired)
   */
  public isSessionValid(): boolean {
    try {
      const login = this.cookieService.get('oadr_login');
      if (!login) return false;

      const loginData: CookieLoginModel = JSON.parse(login);
      if (!loginData.expiresAt) return false;

      // Check if current time is before expiration time
      return Date.now() < loginData.expiresAt;
    } catch (e) {
      return false;
    }
  }

  /**
   * Verify if user is logged in by pinging the API
   * @returns Observable with logged in status
   */
  public isLogged(): Observable<Boolean> {
    return this.httpClient.get(environment.venway3ApiHost + '/api/ping',  { responseType: 'text' }).pipe(map(
      (response: any) => {
        return true;
      },
    ), catchError((err: Error | HttpErrorResponse) => {
      return throwError(() => err);
    }));
  }

  /**
   * Check if current user has admin permissions
   * @returns Boolean indicating if user is admin
   */
  public isAdmin(): boolean {
    try {
      const login = this.cookieService.get('oadr_login');
      if (!login) return false;
      return (JSON.parse(login) as CookieLoginModel).role === 'admin';
    } catch (e) {
      return false;
    }
  }

  /**
   * Validate the current session token with the API
   * @returns Observable with validation result including TTL info
   */
  public validateSession(): Observable<any> {
    const token = this.cookieService.get('enoc_session');
    if (!token) {
      return throwError(() => new Error('No session token found'));
    }

    return this.apiService.validateSession(token).pipe(
      map((response) => {
        if (response.valid) {
          // Optionally update cookie expiration based on remaining TTL
          const expirationDate = new Date(Date.now() + response.session_ttl * 1000);
          this.cookieService.set('enoc_session', token, {
            expires: expirationDate,
            path: '/',
            domain: environment.cookieDomain,
            sameSite: 'Lax',
          });

          // Also update oadr_login cookie
          const login = this.cookieService.get('oadr_login');
          if (login) {
            this.cookieService.set('oadr_login', login, {
              expires: expirationDate,
            });
          }
        }
        return response;
      }),
      catchError((err: Error | HttpErrorResponse) => {
        // Session is invalid, clear cookies
        this.cookieService.delete('enoc_session', '/', environment.cookieDomain);
        this.cookieService.delete('oadr_login');
        return throwError(() => err);
      })
    );
  }
}
